import { createElement, forwardRef, type HTMLAttributes, type ReactNode, type JSX } from "react";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <Typography>. Ported
// letter-for-letter from Typography-L7D756PC.css (read from node_modules,
// not guessed) — same --ds-* references, new class names
// (.rc-typography--*). Implementation matches the vendor's own
// createElement(tag, ...) — variant picks a default semantic element,
// `as` overrides it, exactly like the original.
//
// Stage 6, typography gap: "body-xs" and "metric" are net-new — design.md's
// type scale (altide-security-design-system.md ss8) has "Body small" (13px)
// and "Metric" (28px/700/tabular) with no existing equivalent at any size
// currently in this ramp. Added regardless of the still-undecided heading
// size compression (6e'), which stays a separate, explicitly-gated decision.
export type TypographyVariant =
  | "display"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "body-lg"
  | "body"
  | "body-sm"
  | "body-xs"
  | "caption"
  | "eyebrow"
  | "metric"
  | "mono";

export interface TypographyProps extends HTMLAttributes<HTMLElement> {
  variant?: TypographyVariant;
  as?: keyof JSX.IntrinsicElements;
  muted?: boolean;
  children: ReactNode;
}

const DEFAULT_ELEMENT: Record<TypographyVariant, keyof JSX.IntrinsicElements> = {
  display: "h1",
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  "body-lg": "p",
  body: "p",
  "body-sm": "p",
  "body-xs": "p",
  caption: "span",
  eyebrow: "span",
  metric: "span",
  mono: "code",
};

export const Typography = forwardRef<HTMLElement, TypographyProps>(function Typography(
  { variant = "body", as, muted, className, children, ...rest },
  ref
) {
  const tag = as ?? DEFAULT_ELEMENT[variant];
  return createElement(
    tag,
    {
      ref,
      className: cn("rc-typography", `rc-typography--${variant}`, muted && "rc-typography--muted", className),
      ...rest,
    },
    children
  );
});
